import { ThemeEvents } from '@theme/events';
import {
  abortTextbookPdpChrome,
  armTextbookPdpChrome,
  finishTextbookPdpChrome,
} from './textbook-pdp-chrome-sync.js';

function parseDisplayedMoneyToCents(text) {
  if (!text) return null;
  const normalized = String(text).replace(/,/g, '').match(/(\d+)(?:\.(\d{1,2}))?/);
  if (!normalized) return null;
  const dollars = Number.parseInt(normalized[1], 10);
  const frac = normalized[2] != null ? normalized[2].padEnd(2, '0').slice(0, 2) : '00';
  const centsPart = Number.parseInt(frac, 10);
  if (!Number.isFinite(dollars) || !Number.isFinite(centsPart)) return null;
  return dollars * 100 + centsPart;
}

function extractLeadingMoneyPrefix(text) {
  const s = String(text).trimStart();
  const idx = s.search(/\d/);
  if (idx <= 0) return '';
  return s.slice(0, idx).trimEnd();
}

function ensureCurrencyPrefix(formatted, prefix) {
  if (!prefix) return formatted;
  const t = String(formatted).trimStart();
  if (/\p{Sc}/u.test(t)) return formatted;
  return `${prefix}${t}`;
}

function formatMoneyFromCents(cents) {
  if (typeof window.Shopify?.formatMoney === 'function') {
    const fmt =
      window.Shopify.money_format || window.Shopify.moneyFormat || '${{amount}}';
    try {
      return window.Shopify.formatMoney(cents, fmt);
    } catch {
      // fall through
    }
  }
  return (cents / 100).toFixed(2);
}

function priceHtmlFromFormatted(formatted) {
  const parts = String(formatted).split('.');
  if (parts.length > 1) {
    return `${parts[0]}.<span class="price-cents">${parts[1]}</span>`;
  }
  return `${parts[0] || formatted}.<span class="price-cents">00</span>`;
}

/**
 * Textbook PDP headline (develop-CMRP-updated rules):
 * - 0 or 2+ checkboxes checked → min–max range over all displayed accordion rows (incl. OOS list price).
 * - Exactly 1 checked → that variant’s price.
 * change is listened in capture phase because purchase-checkbox handlers stopPropagation on bubble.
 */
class ProductPrice extends HTMLElement {
  /** @type {string | null} */
  #moneyPrefix = null;

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    if (closestSection.querySelector('.textbook-variant-picker')) {
      armTextbookPdpChrome(this);
    }
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener('click', this.handleUiUpdate);
    closestSection.addEventListener('change', this.handleUiUpdate, true);
    this.scheduleTextbookPriceSync();
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener('click', this.handleUiUpdate);
    closestSection.removeEventListener('change', this.handleUiUpdate, true);
  }

  handleUiUpdate = () => {
    requestAnimationFrame(() => this.syncPriceFromDisplayedTextbookOptions());
  };

  scheduleTextbookPriceSync() {
    requestAnimationFrame(() => this.syncPriceFromDisplayedTextbookOptions());
  }

  #captureMoneyPrefix(priceContainer, referenceEl) {
    if (this.#moneyPrefix != null) return;
    const prefix =
      extractLeadingMoneyPrefix(referenceEl?.textContent ?? '') ||
      extractLeadingMoneyPrefix(priceContainer.querySelector('.price')?.textContent ?? '') ||
      extractLeadingMoneyPrefix(priceContainer.querySelector('.price-range')?.textContent ?? '');
    if (prefix) this.#moneyPrefix = prefix;
  }

  #formattedWithPrefix(cents) {
    return ensureCurrencyPrefix(formatMoneyFromCents(cents), this.#moneyPrefix ?? '');
  }

  /**
   * A picker row the shopper can see. Access-blocked rows stay in the DOM
   * with display:none and must not affect the headline price.
   * @param {Element} option
   */
  static #isDisplayedOption(option) {
    if (!(option instanceof HTMLElement)) return false;
    if (option.hidden || option.getAttribute('hidden') != null) return false;
    if (option.style.display === 'none') return false;
    if (option.getAttribute('data-fa-digital-blocked') === 'true') return false;
    return true;
  }

  /** @param {boolean} visible */
  #setVisible(visible) {
    this.hidden = !visible;
    this.style.display = visible ? '' : 'none';
    if (visible) this.removeAttribute('data-fa-purchase-hidden');
    else this.setAttribute('data-fa-purchase-hidden', 'true');
  }

  /** @param {Element} option */
  static #centsForAccordionOption(option) {
    const cb = option.querySelector('.purchase-checkbox[data-price]');
    if (cb) {
      const n = Number.parseInt(cb.getAttribute('data-price') ?? '', 10);
      if (Number.isFinite(n) && n > 0) return n;
    }
    const parsed = parseDisplayedMoneyToCents(
      option.querySelector('.accordion-variant-price')?.textContent ?? ''
    );
    return parsed != null && parsed > 0 ? parsed : null;
  }

  syncPriceFromDisplayedTextbookOptions() {
    const closestSection = this.closest('.shopify-section, dialog');
    const picker = closestSection?.querySelector('.textbook-variant-picker');
    const gateVisibility = Boolean(picker);
    let hidePrice = false;

    try {
      if (!closestSection || !picker) return;

      const priceContainer = this.querySelector('[ref="priceContainer"]');
      if (!priceContainer) return;

      const allOptions = Array.from(
        picker.querySelectorAll('.accordion-variant-option, .purchase-type'),
      ).filter((option) => ProductPrice.#isDisplayedOption(option));
      if (allOptions.length === 0) {
        hidePrice = true;
        return;
      }

      const checked = Array.from(picker.querySelectorAll('.purchase-checkbox:checked')).filter(
        (cb) => ProductPrice.#isDisplayedOption(cb.closest('.accordion-variant-option, .purchase-type')),
      );

      const centsList = [];
      for (const option of allOptions) {
        const c = ProductPrice.#centsForAccordionOption(option);
        if (c != null) centsList.push(c);
      }
      if (centsList.length === 0) {
        hidePrice = true;
        return;
      }

      let minCents;
      let maxCents;

      // CMRP: single price only when exactly one variant is selected; 0 or 2+ → range again.
      if (checked.length === 1) {
        const cb = checked[0];
        const raw = cb.getAttribute('data-price');
        const fromAttr = raw != null ? Number.parseInt(raw, 10) : NaN;
        if (Number.isFinite(fromAttr) && fromAttr > 0) {
          minCents = maxCents = fromAttr;
        } else {
          const option = cb.closest('.accordion-variant-option');
          const c = option ? ProductPrice.#centsForAccordionOption(option) : null;
          if (c == null) return;
          minCents = maxCents = c;
        }
      } else {
        minCents = Math.min(...centsList);
        maxCents = Math.max(...centsList);
      }

      this.#captureMoneyPrefix(
        priceContainer,
        priceContainer.querySelector('.price') ?? priceContainer.querySelector('.price-range')
      );

      const segment =
        minCents === maxCents
          ? priceHtmlFromFormatted(this.#formattedWithPrefix(minCents))
          : `${priceHtmlFromFormatted(this.#formattedWithPrefix(minCents))} - ${priceHtmlFromFormatted(this.#formattedWithPrefix(maxCents))}`;

      priceContainer.innerHTML = `<span class="price-range">${segment}</span>`;
      this.#setVisible(true);
    } finally {
      if (gateVisibility) finishTextbookPdpChrome(closestSection, this);
      if (hidePrice) this.#setVisible(false);
    }
  }

  updatePrice = (event) => {
    const section = this.closest('.shopify-section, dialog');
    const textbook = section?.querySelector('.textbook-variant-picker');

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      if (textbook) abortTextbookPdpChrome(this);
      return;
    }

    if (textbook) {
      armTextbookPdpChrome(this);
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) {
      this.scheduleTextbookPriceSync();
      return;
    }

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
      this.#moneyPrefix = null;
    }

    this.scheduleTextbookPriceSync();
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
